import { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import { useForm } from "react-hook-form";
import {
  Button,
  CircularProgress,
  debounce,
  // ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { PrescriptionContext } from ".";
import { useMutation } from "react-query";
import {
  getDrugs,
  getUnit,
  getFrequency,
  getDosageTiming,
  getRouteOfAdmin,
  saveOrUpdatePrescription,
  getPrescription,
} from "../../services";
import {
  defaultValues,
  DosageTimingType,
  DrugFrequencyType,
  DrugType,
  DrugunitType,
  preparePrescriptionPayload,
  PrescriptionPayloadType,
  PrescriptionType,
  RouteOfAdminType,
} from "./types";
import moment from "moment";
import { FormOnChangeEventType } from "../../types";
import usePopup from "../../../hooks/usePopup";
import { useParams } from "react-router-dom";
import { HL7ContexTriggerContex } from "../../../context/HL7MessageTrigger/HL7MessageTriggerProvider";
import { EmrContext } from "../../index";
import useAuthentication from "../../../hooks/useAuthentication";
import useDrugFavourite from "../hook/useDrugFavourite";

const AddPrescription: FC<{ close?: () => void }> = ({ close }) => {
  const theme = useTheme();
  const { setTrigger } = useContext(HL7ContexTriggerContex);
  const { encounterId } = useParams();
  const { showSnackbar } = usePopup();
  const { user } = useAuthentication();
  const activitySearchUserId =
    Number(user?.userId ?? user?.login_id) || null;
  const [prescriptionRemarks, setPrescriptionRemarks] = useState<string>("");
  const { edit, setList, setEdit } = useContext(PrescriptionContext);
  const { triggerConsultationStart } = useContext(EmrContext);
  const { control, handleSubmit, reset, watch, setValue, getValues } =
    useForm<PrescriptionType>({
      defaultValues,
    });
  const regex = /(ointment|cream|lotion)/i;
  const allValues = watch();

  const {
    mutate: fetchDrug,
    data: drugs,
    isLoading: drugsLoading,
  } = useMutation(getDrugs);

  const { drugListOpen, setDrugListOpen, renderDrugOptionListItem } =
    useDrugFavourite({ drugOptions: drugs?.response });

  const {
    mutate: fetchUnit,
    data: units,
    isLoading: unitLoading,
  } = useMutation(getUnit, {
    onSuccess: (result) => {
      const drug = getValues("drug");
      const dosageForm =
        drug?.drugDosageForm == "TABLETS" ? "tablet" : drug?.drugDosageForm;
      if (result.responseIndicator == "success" && dosageForm) {
        const unit = result.response.find(
          (item) => item.name.toLowerCase() == dosageForm.toLowerCase(),
        );
        unit && setValue("prescriptionUnit", unit as DrugunitType);
      }
    },
  });
  const {
    mutate: fetchFrequency,
    data: frequency,
    isLoading: frequencyLoading,
  } = useMutation(getFrequency);
  const {
    mutate: fetchDosageTiming,
    data: dosageTiming,
    isLoading: dosageTimingLoading,
  } = useMutation(getDosageTiming);
  const {
    mutate: fetchRouteOfAdmin,
    data: routeOfAdmin,
    isLoading: routeOfAdminLoading,
  } = useMutation(getRouteOfAdmin, {
    onSuccess: (result) => {
      const drug = getValues("drug");
      if (result.responseIndicator == "success" && drug?.drugRoot) {
        const root = result.response.find(
          (item) => item.routeName == drug?.drugRoot,
        );
        root && setValue("routeOfAdmin", root as RouteOfAdminType);
      }
    },
  });

  const { mutate: postPrescription, isLoading: postLoading } = useMutation(
    saveOrUpdatePrescription,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
          });
          triggerConsultationStart();
          resetFields();
          setTrigger({
            encounterId: encounterId as string,
            tab: "prescriptions",
          });
          fetchPrescription(encounterId as string);
          setEdit(null);
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
          });
        }
      },
    },
  );

  const { mutate: fetchPrescription } = useMutation(getPrescription, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        setList(result.response);
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
        });
      }
    },
  });

  const calculateQuantity = () => {

    const currentValues = getValues();
    const dosage = parseInt(currentValues.dosage || "0");
    const frequency = currentValues.frequencyResponse?.freqAmount || 0;
    const duration = parseInt(currentValues.prescriptionDuration || "0");
    const amount = currentValues?.drug?.drugAmount || 0;
    let quantity = dosage * frequency * duration;
    console.log("quantity => ",currentValues.drug)
    if (currentValues.drug?.drugDosageForm.toLowerCase().includes("tablet")) {
      let refill = amount ? Math.floor(quantity / amount) : 0;
      refill = quantity > amount ? Math.floor(quantity / amount) + 1 : 0;
      refill > 1 && (refill -= 1);
      setValue("prescriptionQuantity", quantity.toString());
      setValue("refill", refill.toString());
    } else {
      setValue("prescriptionQuantity", "1");
    }
  };

  const createPrescription = () => {
    const currentValues = getValues();
    const dosage = currentValues.dosage || "";
    const unit = currentValues.prescriptionUnit?.name || "";
    const frequency = currentValues.frequencyResponse?.freqName || "";
    const timing = currentValues.dosageTiming?.doseTiming || "";
    const duration = currentValues.prescriptionDuration || "";
    setPrescriptionRemarks(
      `${
        regex.test(`${currentValues?.drug?.drugGenericName}`) ? `Apply` : "Take"
      } ${dosage} ${unit} ${frequency} ${timing} for ${duration} days`,
    );
  };

  const resetFields = () => {
    reset({
      ...defaultValues,
      startDate: moment().format("YYYY-MM-DD"),
      prescriptionQuantity: "0",
      refill: "0",
    });
  };

  const handleClose = () => {
    close && close();
    setEdit(null);
  };

  const fetchDrugs = (searchText: string) => {
    fetchDrug({ searchText, userId: activitySearchUserId });
  };

  const fetchAllData = () => {
    fetchDrugs("");
    fetchUnit("");
    fetchFrequency("");
    fetchDosageTiming("");
    fetchRouteOfAdmin("");
  };

  const onChange = (e: FormOnChangeEventType) => {
    if (
      fieldForInstruction[e.target.name as keyof typeof fieldForInstruction]
    ) {
      createPrescription();
    }
    if (
      fieldsForCalculateQuantity[
        e.target.name as keyof typeof fieldsForCalculateQuantity
      ]
    ) {
      calculateQuantity();
      if (e.target.name == "drug") {
        fetchRouteOfAdmin(e.target.value.drugRoot);
        fetchUnit(
          e.target.value.drugDosageForm == "TABLETS"
            ? "tablet"
            : e.target.value.drugDosageForm,
        );
        reset({
          drug: e.target.value,
          startDate: moment().format("YYYY-MM-DD"),
        });
      }
    }
  };

  const searchDebounce = debounce(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { name, value } = e.target;
      switch (name) {
        case "drug":
          fetchDrugs(value);
          break;
        case "prescriptionUnit":
          fetchUnit(value);
          break;
        case "frequencyResponse":
          fetchFrequency(value);
          break;
        case "dosageTiming":
          fetchDosageTiming(value);
          break;
        case "routeOfAdmin":
          fetchRouteOfAdmin(value);
          break;
      }
    },
    500,
  );
  const onSubmit = (value: any) => {
    let params: Omit<
      PrescriptionPayloadType,
      "prescriptionClinicId" | "prescriptionCreatedById" | "userType"
    >;
    if (Boolean(edit)) {
      params = preparePrescriptionPayload({
        ...edit,
        ...value,
        prescriptionRemarks,
        createdByDate: null,
        encounterId,
      });
    } else {
      params = preparePrescriptionPayload({
        ...value,
        prescriptionRemarks,
        createdByDate: null,
        encounterId,
      });
      // resetFields();
    }
    postPrescription(params);
  };

  useEffect(() => {
    fetchAllData();
    resetFields();
  }, []);

  useEffect(() => {
    if (Boolean(edit)) {
      reset({
        ...edit,
        refill: edit?.refill.toString(),
      });
      setPrescriptionRemarks(edit?.prescriptionRemarks || "");
    } else {
      resetFields();
    }
  }, [edit]);

  console.log("allValues => ",allValues)

  return (
    <div className="border-2 rounded">
      <div className="border-b  rounded-t p-2" style={{}}>
        <Typography color={theme.palette.primary.main} fontWeight={600}>
          {Boolean(edit) ? "Update" : "Add"} Prescription
        </Typography>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-y-5 gap-x-2 grid-cols-12 p-2"
      >
        <div className="col-span-full">
          <CustomFormField
            name="drug"
            control={control}
            element="autocomplete"
            fieldProps={{ label: "Drug", onChange: searchDebounce }}
            rules={{ required: "Drug required", onChange }}
            options={drugs?.response || []}
            loading={drugsLoading}
            autocompleteProps={{
              open: drugListOpen,
              onOpen: () => setDrugListOpen(true),
              onClose: () => setDrugListOpen(false),
              disableClearable: true,
              filterOptions: (option) => option,
              isOptionEqualToValue: (option: DrugType, value: DrugType) =>
                option.drugId == value.drugId,
              getOptionLabel: (option: DrugType) =>
                `${option.drugCode} | ${option.drugPackageName} ${Boolean(option?.drugAmount) ? "[ " + option?.drugAmount + " ]" : ""}`,
              renderOption: (props, option: DrugType) =>
                renderDrugOptionListItem(
                  props,
                  option,
                  <ListItemText
                    primary={
                      <div className="grid grid-cols-12 items-center gap-2 w-full min-w-0">
                        <div className="col-span-2 gap-1 flex justify-start min-w-0">
                          <strong
                            style={{ color: theme.palette.text.secondary }}
                          >
                            Code :
                          </strong>
                          <Typography fontSize="12px" noWrap>
                            {`${option.drugCode}`}
                          </Typography>
                        </div>
                        <div className="col-span-10 flex gap-2 items-center border-s-2 ps-2 min-w-0">
                          <ListItemText
                            sx={{ minWidth: 0, m: 0 }}
                            primary={
                              <div className="flex gap-2 items-center min-w-0">
                                <strong
                                  className="text-sm flex-shrink-0"
                                  style={{
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  Name{" "}
                                </strong>
                                <strong>:</strong>
                                <Typography
                                  fontSize="12px"
                                  noWrap
                                  sx={{ minWidth: 0, flex: 1 }}
                                >
                                  {` ${option.drugPackageName}, ${option.drugGenericName}`}{" "}
                                  <span className="border-2 rounded p-1">
                                    {option?.drugAmount || ""}
                                  </span>
                                </Typography>
                              </div>
                            }
                            secondary={
                              <Typography
                                color="primary.main"
                                fontSize=".6rem"
                                noWrap
                              >
                                {option.drugDosageForm}
                              </Typography>
                            }
                          />
                        </div>
                      </div>
                    }
                  />,
                ),
            }}
          />
        </div>

        <div className="col-span-4 grid grid-cols-10 gap-2 items-end">
          <div className="pe-1">
            <Typography>
              {regex.test(`${allValues?.drug?.drugGenericName}`)
                ? `Apply`
                : "Take"}
            </Typography>
          </div>
          <div className="col-span-3">
            <CustomFormField
              name="dosage"
              control={control}
              element="input"
              fieldProps={{
                label: "Dosage",
                type: "number",
                variant: "standard",
              }}
              rules={{ required: "Dosage required", onChange }}
            />
          </div>
          <div className="col-span-6">
            <CustomFormField
              name="prescriptionUnit"
              control={control}
              element="autocomplete"
              fieldProps={{
                label: "Unit",
                variant: "standard",
                onChange: searchDebounce,
              }}
              rules={{ required: "Unit required", onChange }}
              options={units?.response || []}
              loading={unitLoading}
              autocompleteProps={{
                filterOptions: (option) => option,
                isOptionEqualToValue: (
                  option: DrugunitType,
                  value: DrugunitType,
                ) => option.id == value.id,
                getOptionLabel: (option: DrugunitType) => option.name,
              }}
            />
          </div>
        </div>

        <div className="col-span-6 grid grid-cols-12 items-end gap-2">
          <div className="text-center">
            <Typography>for</Typography>
          </div>
          <div className="col-span-3">
            <CustomFormField
              name="frequencyResponse"
              control={control}
              element="autocomplete"
              fieldProps={{
                label: "Frequency",
                variant: "standard",
                onChange: searchDebounce,
              }}
              rules={{ required: "Frequency required", onChange }}
              options={frequency?.response || []}
              loading={frequencyLoading}
              autocompleteProps={{
                filterOptions: (option) => option,
                isOptionEqualToValue: (
                  option: DrugFrequencyType,
                  value: DrugFrequencyType,
                ) => option.freqId == value.freqId,
                getOptionLabel: (option: DrugFrequencyType) => option.freqCode,
              }}
            />
          </div>
          <div className="col-span-2 text-center">
            <Typography>times (s)</Typography>
          </div>
          <div className="col-span-6">
            <CustomFormField
              name="dosageTiming"
              control={control}
              element="autocomplete"
              fieldProps={{
                label: "Dosage timing",
                variant: "standard",
                onChange: searchDebounce,
              }}
              rules={{ required: "Dosage timing required", onChange }}
              options={dosageTiming?.response || []}
              loading={dosageTimingLoading}
              autocompleteProps={{
                filterOptions: (option) => option,
                isOptionEqualToValue: (
                  option: DosageTimingType,
                  value: DosageTimingType,
                ) => option.id == value.id,
                getOptionLabel: (option: DosageTimingType) => option.doseTiming,
              }}
            />
          </div>
        </div>

        <div className="col-span-2 gap-2 grid grid-cols-12 items-end">
          <div className="col-span-2">
            <Typography>for</Typography>
          </div>
          <div className="col-span-6">
            <CustomFormField
              name="prescriptionDuration"
              control={control}
              element="input"
              fieldProps={{
                label: "Duration",
                variant: "standard",
                type: "number",
              }}
              rules={{ required: "Duration required", onChange }}
            />
          </div>
          <div className="col-span-2">
            <Typography>day(s)</Typography>
          </div>
        </div>

        <div className="col-span-4 grid grid-cols-12 items-end">
          <div className="col-span-4">
            <Typography>Route of admin:</Typography>
          </div>
          <div className="col-span-8">
            <CustomFormField
              name="routeOfAdmin"
              control={control}
              element="autocomplete"
              fieldProps={{
                label: "Route of admin",
                variant: "standard",
                onChange: searchDebounce,
              }}
              rules={{ required: "Route of admin required" }}
              options={routeOfAdmin?.response || []}
              loading={routeOfAdminLoading}
              autocompleteProps={{
                filterOptions: (option) => option,
                isOptionEqualToValue: (
                  option: RouteOfAdminType,
                  value: RouteOfAdminType,
                ) => option.routeId == value.routeId,
                getOptionLabel: (option: RouteOfAdminType) => option.routeName,
              }}
            />
          </div>
        </div>

        <div className="col-span-3 grid grid-cols-12 items-end">
          <div className="col-span-4">
            <Typography>Refill this for</Typography>
          </div>
          <div className="col-span-4">
            <CustomFormField
              name="refill"
              control={control}
              element="input"
              fieldProps={{
                label: "Refill",
                variant: "standard",
                type: "number",
              }}
              rules={{ required: "Duration required" }}
            />
          </div>
          <div className="col-span-4">
            <Typography>times(s)</Typography>
          </div>
        </div>

        <div className="col-span-4 grid grid-cols-12 items-end">
          <div className="col-span-3 text-center">
            <Typography>start date</Typography>
          </div>
          <div className="col-span-8">
            <CustomFormField
              name="startDate"
              control={control}
              element="datepicker"
              fieldProps={{
                label: "Start date",
                type: "number",
              }}
              rules={{ required: "Duration required" }}
            />
          </div>
        </div>

        <div className="col-span-full grid grid-cols-12 gap-2 items-end">
          <div className="col-span-10">
            <CustomFormField
              name="notes"
              control={control}
              element="input"
              fieldProps={{
                label: "Enter notes",
                multiline: true,
              }}
              rules={{ required: false }}
            />
          </div>
          <div className="col-span-2">
            <CustomFormField
              name="prescriptionQuantity"
              control={control}
              element="input"
              fieldProps={{
                label: "Total quandity",
              }}
              rules={{ required: "Total quandity required" }}
            />
          </div>
        </div>

        <div className="col-span-full flex justify-end items-center gap-3">
          <Button
            disabled={postLoading}
            endIcon={
              postLoading && (
                <CircularProgress
                  size={18}
                  sx={{ color: theme.palette.background.paper }}
                />
              )
            }
            color="success"
            type="submit"
          >
            save
          </Button>
          {close && (
            <Button color="error" onClick={handleClose}>
              cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddPrescription;
const fieldForInstruction = {
  dosage: true,
  prescriptionUnit: true,
  frequencyResponse: true,
  dosageTiming: true,
  prescriptionDuration: true,
};

const fieldsForCalculateQuantity = {
  drug: true,
  dosage: true,
  frequencyResponse: true,
  prescriptionDuration: true,
};
